// Landing light mode (Dan 2026-10-16). The landing is dark by default; the
// pages listed in LIGHT_THEME_PATHS can render light, matching the visitor's
// device setting. The colors live in `styles/global.css`
// (`html[data-theme="light"]`). A page joins the list once it has been
// checked in light (its drawn app pictures wrapped in
// `theme-dark home-app-picture`, no baked-in dark colors left).
//
// LIGHT_FOLLOWS_SYSTEM is on for everyone (Dan 2026-10-16): listed pages
// follow the device. `?theme=light` / `?theme=dark` force either one. Set it
// back to false to make light preview-only again.
//
// Wired in gatsby-ssr (head script, before first paint) and gatsby-browser
// (client-side navigation and device changes).
export const LIGHT_FOLLOWS_SYSTEM = true;

/** Path prefixes that can render light. "/" alone is the homepage. */
export const LIGHT_THEME_PATHS = [
  "/",
  // Style outright: text pages with no drawn app pictures.
  "/help",
  "/blog",
  "/creators",
  "/terms",
  "/privacy",
  "/seller-terms",
  "/referral-terms",
  "/whats-new",
  "/careers",
  "/enterprise",
  "/testimonials",
  "/reviews",
  "/movement",
  "/downloads",
  "/mobile",
  "/tools",
  // Quick check: one or two pictures each, checked in light.
  "/pricing",
  "/marketplace",
  "/kits",
  "/affiliates",
  "/affiliate-apply",
  "/referrals",
  "/partnerships",
  "/community",
  "/bootcamp",
  "/bootcamp-alumni",
  // One template (VsPageV2): its pictures are wrapped in SplitSection and
  // the hero scene section.
  "/vs",
  // Feature pages: pictures sit in SplitSection, TabbedProductSection, or
  // the Hero's productPreview, all wrapped; the community ones draw their
  // own light mode (`.community-ui`).
  "/features",
  "/teams",
  "/sprint",
  "/workshop",
  // The viral topics report's opt-in and thank-you pages, built in theme tokens.
  "/viral",
  // Workflow guides (WorkflowGuidePage): the hero preview is wrapped.
  "/workflows",
  // The saver page (components/save/): built in theme tokens, pictures wrapped.
  "/save",
  // The paths homepage's own address (it's normally served at "/").
  "/home-paths",
] as const;

/**
 * The visitor's pick from the switch (components/ThemeSwitch.tsx), kept only
 * while it differs from the device. Renamed from "eden-theme" (2026-10-24):
 * the old key kept any click forever, so one tap on the sun pinned dark-mode
 * devices to light. The rename drops those stale picks.
 */
export const THEME_STORAGE_KEY = "eden-theme-pick";
/** Fired on window whenever the theme is (re)applied. */
export const THEME_CHANGE_EVENT = "eden-theme-change";

const LIGHT_THEME_COLOR = "#fcfcfa";
const DARK_THEME_COLOR = "#0a0a0a";

export function isLightThemePath(pathname: string): boolean {
  return LIGHT_THEME_PATHS.some((prefix) =>
    prefix === "/" ? pathname === "/" : pathname === prefix || pathname.startsWith(`${prefix}/`),
  );
}

function pickTheme(location: Location, prefersLight: boolean): "light" | "dark" {
  if (!isLightThemePath(location.pathname)) return "dark";
  const forced = new URLSearchParams(location.search).get("theme");
  if (forced === "light" || forced === "dark") return forced;
  const stored = readStoredTheme();
  if (stored) return stored;
  return LIGHT_FOLLOWS_SYSTEM && prefersLight ? "light" : "dark";
}

function readStoredTheme(): "light" | "dark" | null {
  try {
    const value = window.localStorage.getItem(THEME_STORAGE_KEY);
    return value === "light" || value === "dark" ? value : null;
  } catch {
    return null;
  }
}

function applyTheme(theme: "light" | "dark") {
  const root = document.documentElement;
  if (theme === "light") root.dataset.theme = "light";
  else delete root.dataset.theme;
  document
    .querySelector('meta[name="theme-color"]')
    ?.setAttribute("content", theme === "light" ? LIGHT_THEME_COLOR : DARK_THEME_COLOR);
  window.dispatchEvent(new Event(THEME_CHANGE_EVENT));
}

/**
 * Runs in every page's <head> before first paint, so a light visitor never
 * sees a dark flash. Same rule as `pickTheme` (link param, then the saved
 * pick, then the device), inlined.
 */
export const THEME_HEAD_SCRIPT = `(function(){try{var P=${JSON.stringify(LIGHT_THEME_PATHS)};var p=location.pathname;var ok=P.some(function(x){return x==="/"?p==="/":p===x||p.indexOf(x+"/")===0;});if(!ok)return;var f=new URLSearchParams(location.search).get("theme");if(f!=="light"&&f!=="dark"){try{f=localStorage.getItem("${THEME_STORAGE_KEY}");}catch(e){}}var l=f==="light"||(f!=="dark"&&${LIGHT_FOLLOWS_SYSTEM}&&window.matchMedia("(prefers-color-scheme: light)").matches);if(l){document.documentElement.dataset.theme="light";var m=document.querySelector('meta[name="theme-color"]');if(m)m.setAttribute("content","${LIGHT_THEME_COLOR}");}}catch(e){}})();`;

let media: MediaQueryList | null = null;

/**
 * Re-applies the theme after a client-side navigation (the head script only
 * runs on a full page load), so leaving a light page for a dark one goes
 * dark, and coming back goes light again. The first call also starts
 * following the device setting while the tab is open.
 */
export function syncThemeForLocation() {
  if (typeof window === "undefined") return;
  if (!media) {
    media = window.matchMedia("(prefers-color-scheme: light)");
    media.addEventListener("change", () =>
      applyTheme(pickTheme(window.location, media?.matches ?? false)),
    );
  }
  applyTheme(pickTheme(window.location, media.matches));
}

/**
 * The switch's click: saves the pick for this browser and applies it now.
 * A saved pick beats the device setting on every light-capable page. Picking
 * the side the device is already on clears it, so the page follows the
 * device again.
 */
export function setThemePreference(theme: "light" | "dark") {
  try {
    const deviceTheme = window.matchMedia("(prefers-color-scheme: light)").matches
      ? "light"
      : "dark";
    if (LIGHT_FOLLOWS_SYSTEM && theme === deviceTheme) {
      window.localStorage.removeItem(THEME_STORAGE_KEY);
    } else {
      window.localStorage.setItem(THEME_STORAGE_KEY, theme);
    }
  } catch {
    // Private mode or blocked storage: still switch this page.
  }
  applyTheme(theme);
}
